import type { PanelDesign, PanelRef, Vec2, WallGrid, WallMetrics } from './types';
import { requireShape, type ShapeDefinition } from './shapes/registry';

/** Wall-level layout: turning a grid plus a shape into concrete panel placements. */

export interface WallLayout {
  shape: ShapeDefinition;
  metrics: WallMetrics;
  panels: PanelRef[];
  /** Bounding box of one panel footprint, in panel space. */
  footprint: { minX: number; minY: number; maxX: number; maxY: number };
}

export function boundingBox(outline: readonly Vec2[]): {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
} {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const point of outline) {
    if (point.x < minX) minX = point.x;
    if (point.y < minY) minY = point.y;
    if (point.x > maxX) maxX = point.x;
    if (point.y > maxY) maxY = point.y;
  }
  return { minX, minY, maxX, maxY };
}

export function buildWallLayout(
  shapeId: string,
  grid: WallGrid,
  size: number,
  gap: number,
): WallLayout {
  const shape = requireShape(shapeId);
  const metrics = shape.metrics(grid, size, gap);
  const panels: PanelRef[] = [];

  for (let row = 0; row < grid.rows; row += 1) {
    for (let column = 0; column < grid.columns; column += 1) {
      const centre = shape.layout(column, row, size, gap);
      panels.push({
        column,
        row,
        centerX: centre.x,
        centerY: centre.y,
        size,
        rotation: shape.orientation?.(column, row) ?? 0,
      });
    }
  }

  return { shape, metrics, panels, footprint: boundingBox(shape.outline(size, gap)) };
}

export function layoutForDesign(design: PanelDesign): WallLayout {
  return buildWallLayout(design.panel.shapeId, design.grid, design.panel.size, design.panel.gap);
}
